/* AddProductionNoteDialog — quick free-text note for the production tech.
 *
 * Drops a note into `production_stage_history` as a non-mutating entry
 * (status="pending", from_stage === to_stage, is_note=true) so the audit
 * trail captures shift handoffs, observations, "saved the failed sheet
 * for QC review", etc. without advancing or rewinding the stage.
 *
 * Backend: POST /api/production/cases/{id}/note.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Note } from "@phosphor-icons/react";


export default function AddProductionNoteDialog({ open, caseId, currentStageLabel, onClose, onAdded }) {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) setNote("");
    }, [open]);

    const submit = async () => {
        const text = (note || "").trim();
        if (text.length < 2) {
            toast.error("Note is too short.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/production/cases/${caseId}/note`, { note: text });
            toast.success("Note added to the audit trail");
            onAdded?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to add note");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="add-production-note-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Note size={16} weight="duotone" /> Add a production note
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        Captured against the current stage{currentStageLabel ? ` (${currentStageLabel})` : ""}.
                        Visible in the case audit timeline. Doesn't advance the workflow.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-1">
                    <div>
                        <Label className="text-xs font-semibold">Note</Label>
                        <Textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows={4}
                            maxLength={2000}
                            placeholder="e.g. Resin batch shifted to lot B-204 mid-run; first 4 sheets pulled clean — flagging for QC focus on canine wells."
                            className="mt-1"
                            data-testid="production-note-textarea"
                        />
                        <div className="text-[10px] text-muted-foreground mt-1 text-right tabular-nums">
                            {note.length} / 2000
                        </div>
                    </div>
                </div>
                <DialogFooter className="mt-2">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={busy || note.trim().length < 2}
                        data-testid="production-note-submit"
                    >
                        {busy ? "Saving…" : "Add to timeline"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
